import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import ShoppingListProductToolbarActions from './ShoppingListProductToolbarActions';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';

interface SortOption {
  value: string;
  label: string;
}

const shoppingListSortOptions: SortOption[] = [
  { value: 'sortOrder-asc', label: 'User Sort Order' },
  { value: 'itemNum-asc', label: 'Item # - Ascending' },
  { value: 'itemNum-desc', label: 'Item # - Descending' },
  { value: 'description-asc', label: 'Description - Ascending' },
  { value: 'description-desc', label: 'Description - Descending' },
  { value: 'price-asc', label: 'Price - Ascending' },
  { value: 'price-desc', label: 'Price - Descending' },
];

const getSortField = (value: string): string => {
  if (value.startsWith('sortOrder-')) return 'sortOrder';
  if (value.startsWith('itemNum-')) return 'itemNum';
  if (value.startsWith('description-')) return 'description';
  if (value.startsWith('price-')) return 'price';
  return 'sortOrder';
};

const getSortDir = (value: string): 'asc' | 'desc' => (value.endsWith('-asc') ? 'asc' : 'desc');

const ShoppingListCardGridHeader: FC = () => {
  const { t } = useTranslation();
  const dataState = useContextSelector(ShoppingListProductsContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onDataStateChange
  );

  const translatedSortOptions = useMemo(
    () => shoppingListSortOptions.map((opt) => ({ ...opt, label: t(opt.label) })),
    [t]
  );

  const selectedSort = useMemo(() => {
    const [defaultOption] = translatedSortOptions;
    if (!dataState?.sort || dataState.sort.length === 0) {
      return defaultOption;
    }
    const [currentSort] = dataState.sort;
    const { field, dir = 'asc' } = currentSort;
    const value = `${field}-${dir}`;
    return translatedSortOptions.find((opt) => opt.value === value) ?? defaultOption;
  }, [dataState?.sort, translatedSortOptions]);

  const handleSortChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const sortOption = e.value as SortOption;
      const field = getSortField(sortOption.value);
      const dir = getSortDir(sortOption.value);

      onDataStateChange({
        ...dataState,
        sort: [{ field, dir }],
        skip: 0,
      });
    },
    [dataState, onDataStateChange]
  );

  return (
    <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2 bg-[#233843] px-2 py-1">
      <ShoppingListProductToolbarActions
        rightContent={
          <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
            <label
              htmlFor="shopping-list-sort-by-dropdown"
              className="text-white text-sm whitespace-nowrap"
            >
              {t('Sort by')}
            </label>
            <DropDownList
              id="shopping-list-sort-by-dropdown"
              className="k-picker-solid product-filter-dropdown"
              style={{ width: 200 }}
              data={translatedSortOptions}
              textField="label"
              dataItemKey="value"
              value={selectedSort}
              onChange={handleSortChange}
              popupSettings={{ className: 'toolbar-dropdown-popup' }}
            />
          </div>
        }
      />
    </div>
  );
};

export default ShoppingListCardGridHeader;
